import React, { useEffect, useState } from 'react';
import {
  CardContent,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { InfoCard, Progress } from '@backstage/core-components';

const useStyles = makeStyles(theme => ({
  tokenField: {
    fontFamily: 'monospace',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  description: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  copyButton: {
    marginRight: theme.spacing(1),
  },
}));

export const AccessTokenCard = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const [token, setToken] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [showToken, setShowToken] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const credentials = await identityApi.getCredentials();
        if (credentials.token) {
          setToken(credentials.token);
        }
      } catch (error) {
        // Only log in development, not production
        if (process.env.NODE_ENV !== 'production') {
          console.error('Failed to retrieve token:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchToken();
  }, [identityApi]);

  const handleToggleVisibility = () => {
    setShowToken(prev => !prev);
  };

  const handleCopyToken = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to copy token:', error);
      }
    }
  };

  if (loading) {
    return <Progress />;
  }

  return (
    <InfoCard title="Access Token">
      <CardContent>
        <Typography className={classes.description}>
          Use this token to authenticate with the Backstage API. Keep it secure
          and do not share it publicly.
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          value={token}
          type={showToken ? 'text' : 'password'}
          className={classes.tokenField}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="copy token"
                  onClick={handleCopyToken}
                  edge="end"
                  className={classes.copyButton}
                  title="Copy to clipboard"
                >
                  <FileCopyIcon />
                </IconButton>
                <IconButton
                  aria-label="toggle token visibility"
                  onClick={handleToggleVisibility}
                  edge="end"
                  title={showToken ? 'Hide token' : 'Show token'}
                >
                  {showToken ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {copySuccess && (
          <Typography variant="body2" color="primary">
            Token copied to clipboard!
          </Typography>
        )}
      </CardContent>
    </InfoCard>
  );
};
